<script setup lang="ts">
import { Plus } from 'lucide-vue-next'

import ContestMechanicPicker from './ContestMechanicPicker.vue'
import ContestObjectiveDetail from './ContestObjectiveDetail.vue'
import ContestPreviewPanel from './ContestPreviewPanel.vue'

import type { ContestMechanic } from '@/types/global'
import type { TemplateData } from '@/types/types'

const props = defineProps<{
  isOpen: boolean
  templates: TemplateData[]
  selectedTemplate?: TemplateData
  urlParams?: Record<string, string>
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { applyTemplate, templatesByModule } = useTemplate()
const { send } = useEvent()

const step = ref<1 | 2>(1)
const slideDirection = ref<'forward' | 'backward'>('forward')
const selectedMechanic = ref<ContestMechanic>('fortuneWheel')
const hasPickedMechanic = ref(false)
const pickedTemplate = ref<TemplateData>()

const stepTransition = computed(() => (slideDirection.value === 'forward' ? 'step-next' : 'step-prev'))

const pickedMechanic = computed(() => (hasPickedMechanic.value ? selectedMechanic.value : undefined))

const createParams = computed(() => ({
  ...props.urlParams,
  ...(pickedMechanic.value && { mechanic: pickedMechanic.value }),
  selectRewards: '1'
}))

const startOnTemplate = (template?: TemplateData) => {
  if (!template) return

  pickedTemplate.value = template
  hasPickedMechanic.value = false
  step.value = 2
}

watch(() => props.selectedTemplate, startOnTemplate)

watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen) return

    startOnTemplate(props.selectedTemplate)

    const { templateFromSuccessPage, ...query } = route.query
    const templateId = Number(templateFromSuccessPage)
    if (!templateId) return

    startOnTemplate(props.templates.find((template) => template.id === templateId))
    router.replace({ query })
  },
  { immediate: true }
)

const startFromScratch = () => {
  applyTemplate('contests', createParams.value)
}

const applySelectedTemplate = (template: TemplateData) => {
  send('segment', 'contests.template:new:clicked', { templateId: template.id, module: 'contests' })
  applyTemplate('contests', { ...createParams.value, templateId: template.id })
}

const createContest = () => {
  if (pickedTemplate.value) applySelectedTemplate(pickedTemplate.value)
  else startFromScratch()
}

const goToObjective = () => {
  hasPickedMechanic.value = true
  pickedTemplate.value ??= props.templates[0]
  slideDirection.value = 'forward'
  step.value = 2
}

const handleNext = () => {
  if (step.value === 1) goToObjective()
  else createContest()
}

const handleClose = () => {
  if (step.value === 1) {
    emit('close')
    return
  }

  pickedTemplate.value = undefined
  slideDirection.value = 'backward'
  step.value = 1
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="t('pages.contests.modal.title')"
    size="6xl"
    :link-text="step === 2 ? t('pages.contests.mechanics.create') : t('global.words.next')"
    :cancel="step > 1 ? t('global.words.previous') : null"
    @click="handleNext"
    @close="handleClose"
  >
    <div class="overflow-hidden">
      <Transition :name="stepTransition" mode="out-in">
        <div :key="step" class="flex flex-col gap-3">
          <p class="font-semibold text-foreground">
            {{ step === 1 ? t('pages.contests.modal.buttonTitle') : t('pages.contests.modal.howToStart') }}
          </p>

          <div class="flex flex-col gap-6 lg:h-[min(674px,calc(90vh-13rem))] lg:flex-row">
            <div class="-my-2 min-h-0 flex-1 overflow-y-auto break-words py-2 text-left">
              <ContestMechanicPicker v-if="step === 1" v-model="selectedMechanic" />

              <div v-else class="grid w-full auto-rows-fr grid-cols-2 grid-rows-[1fr_auto] gap-4 sm:grid-cols-3">
                <button
                  type="button"
                  class="flex flex-col items-center justify-center gap-3 rounded-xl border p-4 transition-[border-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border motion-reduce:transition-none"
                  :class="
                    pickedTemplate
                      ? 'border-dashed border-stone-300 bg-white hover:border-stone-400 hover:shadow-card-hover'
                      : 'border-primary bg-white'
                  "
                  :aria-pressed="!pickedTemplate"
                  @click="pickedTemplate = undefined"
                >
                  <span class="rounded-md bg-muted p-2">
                    <Plus class="size-5" :stroke-width="1.5" />
                  </span>
                  <span class="text-sm font-medium text-foreground">{{ t('pages.contests.modal.skipObjective') }}</span>
                </button>

                <p v-if="templates.length" class="col-span-full -mb-1 mt-2 text-sm text-foreground-secondary">
                  {{ t('pages.contests.modal.withObjective') }}
                </p>

                <BaseTemplateCard
                  v-for="template in templates"
                  :key="template.id"
                  :title="template.name"
                  :icon="templatesByModule.contests.icon"
                  :is-selected="pickedTemplate?.id === template.id"
                  @click="pickedTemplate = template"
                />
              </div>
            </div>

            <div class="w-full shrink-0 rounded-xl border border-border bg-muted/30 lg:w-[372px] lg:overflow-y-auto">
              <ContestPreviewPanel v-if="step === 1" class="px-2 pt-3" :mechanic="selectedMechanic" />
              <ContestObjectiveDetail v-else class="p-4" :mechanic="pickedMechanic" :template="pickedTemplate" />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </BaseModal>
</template>

<style scoped>
.step-next-enter-active,
.step-next-leave-active,
.step-prev-enter-active,
.step-prev-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.step-next-enter-from {
  opacity: 0;
  transform: translateX(28px);
}
.step-next-leave-to {
  opacity: 0;
  transform: translateX(-28px);
}

.step-prev-enter-from {
  opacity: 0;
  transform: translateX(-28px);
}
.step-prev-leave-to {
  opacity: 0;
  transform: translateX(28px);
}

@media (prefers-reduced-motion: reduce) {
  .step-next-enter-active,
  .step-next-leave-active,
  .step-prev-enter-active,
  .step-prev-leave-active {
    transition: none;
  }

  .step-next-enter-from,
  .step-next-leave-to,
  .step-prev-enter-from,
  .step-prev-leave-to {
    transform: none;
  }
}
</style>
